import { Box } from '@mui/material';
import MenuSidebar from './MenuSidebar';
import MenuContent from './MenuContent';
import { useState, useEffect } from 'react';
import { useMenuData } from '../../../hooks/useMenuData';
import MenuLayoutSkeleton from './MenuLayoutSkeleton';
import usePublishEvent from '../../../hooks/usePublishEvent';
import { useUser } from '../../../hooks/useUser';
import NoDataFallback from '../../common/NoDataFallback';

const MenuLayout = ({
  selectedStore,
  fromStoreDetails = false,
  searchQuery = '',
}: {
  selectedStore: any;
  fromStoreDetails?: boolean;
  searchQuery?: string;
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('');
  const [menuParams, setMenuParams] = useState({
    folderPath: '',
    fileName: '',
    tenantName: '',
  });
  const [hasInitialized, setHasInitialized] = useState(false);
  const [previousSearchQuery, setPreviousSearchQuery] = useState('');

  const { user, isLoading: isUserLoading } = useUser();

  const {
    data: publishEvents,
    dataLoading: publishLoading,
    error: publishError,
  } = usePublishEvent({ selectedStore });

  // Extract folder path and file name from publishEvents data
  useEffect(() => {
    if (publishEvents?.data && Array.isArray(publishEvents.data)) {
      // Find all events that match our criteria
      const relevantEvents = publishEvents.data.filter(
        (event: any) =>
          event.status === 'COMPLETED' &&
          event.stage === 'TRANSFORMED' &&
          event.target_path !== null
      );

      // Sort by timestamp (assuming there's a created_at or updated_at field)
      // If no such field exists, you'll need to modify this to use the appropriate timestamp field
      const sortedEvents = relevantEvents.sort((a: any, b: any) => {
        // Assuming there's a created_at or updated_at field
        // Replace 'created_at' with the actual timestamp field name in your data
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      });

      // Get the most recent event
      const relevantEvent = sortedEvents.length > 0 ? sortedEvents[0] : null;

      // If not found, try with a more relaxed criteria (any status_id for stage_id = 2)
      if (!relevantEvent) {
        console.log(
          'No event found with original criteria, trying with relaxed criteria'
        );
      }

      if (relevantEvent && relevantEvent.target_path) {
        // Parse the target_path to extract folder path and file name
        const path = relevantEvent.target_path;

        // Handle new format: s3://quibble-client/pos-menu-transformed/example/6/menu.json
        const newPathRegex = /s3:\/\/quibble-client\/(.+)\/([^/]+)$/;
        const matches = path.match(newPathRegex);

        let newMenuParams = {
          folderPath: '',
          fileName: '',
          tenantName: user?.tenantName || '',
        };

        if (matches && matches.length >= 3) {
          const folderPath = matches[1];
          const fileName = matches[2];

          newMenuParams = {
            folderPath: folderPath,
            fileName: fileName,
            tenantName: user?.tenantName || '',
          };
        }

        if (newMenuParams.folderPath !== '' && newMenuParams.fileName !== '') {
          setMenuParams(newMenuParams);
        }
      }
    }
  }, [publishEvents, user?.tenantName]);

  const hasValidParams =
    menuParams.folderPath !== '' && menuParams.fileName !== '';

  const {
    data: menuData,
    isLoading: menuLoading,
    error: menuError,
  } = useMenuData(
    menuParams,
    !!publishEvents?.data && hasValidParams,
    selectedStore
  );

  // When new menuData loads, set first category and subcategory (only on initial load)
  useEffect(() => {
    if (menuData?.results?.length > 0 && !hasInitialized) {
      const firstCategory = menuData.results[0];
      setSelectedCategory(firstCategory.title);

      // If the first category has subcategories, select the first subcategory
      if (firstCategory.subcategories?.length > 0) {
        setSelectedSubcategory(firstCategory.subcategories[0].title);
      } else {
        setSelectedSubcategory('');
      }
      setHasInitialized(true);
    }
  }, [menuData, hasInitialized]);

  // Show loading state when any data is still loading or the necessary data is not available
  const isLoading =
    publishLoading || menuLoading || isUserLoading || !selectedStore;

  // Determine if there's an error or no data available
  const hasError =
    publishError ||
    menuError ||
    menuData === undefined ||
    (!isLoading &&
      (publishEvents?.data?.length === 0 ||
        !selectedStore?.is_menu_configured)) ||
    menuData?.results?.length === 0;

  // Enhanced search function that searches across categories and subcategories
  const getFilteredMenuData = () => {
    if (!searchQuery || !menuData?.results) return menuData;

    const searchLower = searchQuery.toLowerCase();

    const filteredResults = menuData.results
      .map((category: any) => {
        // Check if category title matches search
        const categoryMatches = category.title
          .toLowerCase()
          .includes(searchLower);

        // Filter items in main category
        const filteredMainItems = category.items.filter(
          (item: any) =>
            item.title.toLowerCase().includes(searchLower) ||
            item.description.toLowerCase().includes(searchLower)
        );

        // Filter subcategories and their items
        const filteredSubcategories =
          category.subcategories
            ?.map((subcategory: any) => {
              const subcategoryMatches = subcategory.title
                .toLowerCase()
                .includes(searchLower);

              const filteredSubItems =
                subcategory.items?.filter(
                  (item: any) =>
                    item.title.toLowerCase().includes(searchLower) ||
                    item.description.toLowerCase().includes(searchLower)
                ) || [];

              return {
                ...subcategory,
                items: filteredSubItems,
                _hasMatchingItems:
                  filteredSubItems.length > 0 || subcategoryMatches,
              };
            })
            .filter(
              (subcategory: any) =>
                subcategory._hasMatchingItems || subcategory.items.length > 0
            ) || [];

        // Include category if it has matching items, matching title, or matching subcategories
        const hasMatchingItems =
          filteredMainItems.length > 0 || categoryMatches;
        const hasMatchingSubcategories = filteredSubcategories.length > 0;

        if (hasMatchingItems || hasMatchingSubcategories) {
          return {
            ...category,
            items: filteredMainItems,
            subcategories: filteredSubcategories,
            _hasMatchingItems: hasMatchingItems,
            _hasMatchingSubcategories: hasMatchingSubcategories,
          };
        }
        return null;
      })
      .filter((category: any) => category !== null);

    return {
      ...menuData,
      results: filteredResults,
    };
  };

  const filteredMenuData = getFilteredMenuData();

  // Update selected category when search results change - but respect user selections
  useEffect(() => {
    // Only run this effect when search query actually changes
    if (searchQuery !== previousSearchQuery) {
      if (searchQuery && filteredMenuData?.results?.length > 0) {
        // Check if current selection is still valid in filtered results
        const currentCategoryExists = filteredMenuData.results.some(
          (cat: any) => cat.title === selectedCategory
        );

        // Only auto-select first category if current selection is not valid
        if (!currentCategoryExists) {
          const firstMatchingCategory = filteredMenuData.results.find(
            (cat: any) => cat._hasMatchingItems || cat._hasMatchingSubcategories
          );

          if (firstMatchingCategory) {
            setSelectedCategory(firstMatchingCategory.title);

            // If the category has matching subcategories, select the first one
            if (firstMatchingCategory.subcategories?.length > 0) {
              setSelectedSubcategory(
                firstMatchingCategory.subcategories[0].title
              );
            } else {
              setSelectedSubcategory('');
            }
          }
        }
      } else if (!searchQuery && previousSearchQuery && hasInitialized) {
        // Only reset to first category when search is cleared (not on initial load)
        if (menuData?.results?.length > 0) {
          setSelectedCategory(menuData.results[0].title);
          if (menuData.results[0].subcategories?.length > 0) {
            setSelectedSubcategory(menuData.results[0].subcategories[0].title);
          } else {
            setSelectedSubcategory('');
          }
        }
      }

      setPreviousSearchQuery(searchQuery);
    }
  }, [
    searchQuery,
    filteredMenuData,
    menuData,
    selectedCategory,
    hasInitialized,
    previousSearchQuery,
  ]);

  // Handle category selection
  const handleCategorySelect = (category: string, subcategory?: string) => {
    setSelectedCategory(category);

    if (subcategory) {
      setSelectedSubcategory(subcategory);
    } else {
      // When selecting a main category, check if it has subcategories
      const selectedCat = menuData?.results?.find(
        (cat: any) => cat.title === category
      );
      if (selectedCat?.subcategories?.length > 0) {
        setSelectedSubcategory(selectedCat.subcategories[0].title);
      } else {
        setSelectedSubcategory('');
      }
    }
  };

  // Show skeleton while loading
  if (isLoading) {
    return <MenuLayoutSkeleton />;
  }

  // Show fallback for errors or no data
  if (hasError) {
    return (
      <NoDataFallback
        message="Data is not available for this store."
        description="Menu is not ingested on this store yet. Please Push Menu to this store to see the Menu."
      />
    );
  }

  // Only show content when we actually have data
  if (!menuData?.results?.length) {
    return <MenuLayoutSkeleton />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        border: !fromStoreDetails ? '1px solid' : 'none',
        borderColor: !fromStoreDetails ? 'lightGray.main' : 'transparent',
        borderRadius: !fromStoreDetails ? '8px' : '0',
        bgcolor: 'background.paper',
        overflow: 'hidden',
        '& ::-webkit-scrollbar': {
          display: 'none',
        },
        '-ms-overflow-style': 'none',
        scrollbarWidth: 'none',
      }}
    >
      <MenuSidebar
        menuData={filteredMenuData}
        selectedCategory={selectedCategory}
        selectedSubcategory={selectedSubcategory}
        onCategorySelect={handleCategorySelect}
        searchQuery={searchQuery}
      />
      <MenuContent
        menuData={filteredMenuData}
        selectedCategory={selectedCategory}
        selectedSubcategory={selectedSubcategory}
        store={selectedStore}
        searchQuery={searchQuery}
      />
    </Box>
  );
};

export default MenuLayout;
